/**
 * Three Column Slider Module
 *
 * @package EVT Restaurant Builder
 * @since 1.0.0
 */

/**
 * Initialize Three Column Slider pattern with mobile slider
 * Desktop: 3 columns in a grid
 * Mobile: Swiper slider
 */
export function initThreeColumnSlider() {
    const sliderContainers = document.querySelectorAll('.three-column-slider');

    if (!sliderContainers.length) {
        return;
    }

    // Function to check if we're on mobile
    function isMobile() {
        return window.innerWidth < 1024; // lg breakpoint
    }

    // Function to initialize Swiper on mobile
    function initMobileSlider(container) {
        const swiperContainer = container.querySelector('.three-column-slider__container');
        
        if (!swiperContainer) {
            return;
        }

        // Check if already initialized
        if (swiperContainer.swiper) {
            return;
        }

        // Only initialize on mobile
        if (!isMobile()) {
            return;
        }

        // Check if Swiper is available
        if (typeof Swiper === 'undefined') {
            console.warn('Swiper library not loaded. Three column slider will display as static grid.');
            return;
        }

        // Remove wp-block-column class from slides to prevent conflicts
        const columns = swiperContainer.querySelectorAll('.three-column-slider__column');
        columns.forEach(column => {
            if (column.classList.contains('wp-block-column')) {
                column.classList.remove('wp-block-column');
                column.setAttribute('data-was-wp-column', 'true');
            }
        });

        // Add pagination element if it doesn't exist
        let pagination = swiperContainer.querySelector('.swiper-pagination');
        if (!pagination) {
            pagination = document.createElement('div');
            pagination.className = 'swiper-pagination';
            swiperContainer.appendChild(pagination);
        }

        // Initialize Swiper
        const swiper = new Swiper(swiperContainer, {
            slidesPerView: 1.1,
            spaceBetween: 20,
            centeredSlides: false,
            
            // Pagination
            pagination: {
                el: pagination,
                clickable: true,
                dynamicBullets: false,
            },

            // Accessibility
            a11y: {
                prevSlideMessage: 'Previous column',
                nextSlideMessage: 'Next column',
                firstSlideMessage: 'This is the first column',
                lastSlideMessage: 'This is the last column',
            },

            // Keyboard navigation
            keyboard: {
                enabled: true,
                onlyInViewport: true,
            },

            // Touch gestures
            touchRatio: 1,
            threshold: 10,

            // Breakpoints - disable slider on desktop
            breakpoints: {
                1024: {
                    enabled: false,
                    slidesPerView: 3,
                },
            },
        });

        // When a brand bleeds the slider into the right gutter (negative margin-right),
        // pad the end of the track so the last slide stops aligned with the first
        // instead of flush against the viewport edge.
        if (parseFloat(getComputedStyle(container).marginRight) < 0) {
            const alignLastSlide = () => {
                const slideWidth = swiper.slidesSizesGrid[0] || 0;
                const offset = Math.max(0, swiper.width - slideWidth);

                if (swiper.params.slidesOffsetAfter !== offset) {
                    swiper.params.slidesOffsetAfter = offset;
                    swiper.update();
                }
            };

            alignLastSlide();
            swiper.on('resize', alignLastSlide);
        }

        // Store swiper instance
        swiperContainer.swiper = swiper;
    }

    // Function to destroy Swiper on desktop
    function destroyMobileSlider(container) {
        const swiperContainer = container.querySelector('.three-column-slider__container');
        
        if (!swiperContainer || !swiperContainer.swiper) {
            return;
        }

        // Destroy on desktop
        if (!isMobile()) {
            const pagination = swiperContainer.querySelector('.swiper-pagination');
            if (pagination) {
                pagination.remove();
            }
            
            if (swiperContainer.swiper && typeof swiperContainer.swiper.destroy === 'function') {
                swiperContainer.swiper.destroy(true, true);
                swiperContainer.swiper = null;
            }

            // Restore wp-block-column class on desktop
            const columns = swiperContainer.querySelectorAll('.three-column-slider__column');
            columns.forEach(column => {
                if (column.getAttribute('data-was-wp-column') === 'true') {
                    column.classList.add('wp-block-column');
                    column.removeAttribute('data-was-wp-column');
                }
            });
        }
    }

    // Initialize for each slider container
    sliderContainers.forEach(container => {
        initMobileSlider(container);
    });

    // Handle resize with debounce
    let resizeTimer;
    window.addEventListener('resize', function() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            sliderContainers.forEach(container => {
                const wasMobile = container.querySelector('.three-column-slider__container')?.swiper;
                const nowMobile = isMobile();

                if (!wasMobile && nowMobile) {
                    // Transitioning from desktop to mobile - initialize slider
                    initMobileSlider(container);
                } else if (wasMobile && !nowMobile) {
                    // Transitioning from mobile to desktop - destroy slider
                    destroyMobileSlider(container);
                }
            });
        }, 250);
    });
}
